import React, { useState, useEffect } from 'react';
import { useUser } from '@clerk/clerk-react';
import { 
  CurrencyDollarIcon, 
  ClockIcon, 
  ArrowUpIcon, 
  ArrowDownIcon,
  CreditCardIcon,
  LightBulbIcon
} from '../utils/icons';

// Helper function to format currency
const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(amount);
};

// Mock data for debt accounts
const mockDebtAccounts = [
  {
    id: 'cc1',
    name: 'Citi Credit Card',
    type: 'credit card',
    last4: '5678',
    balance: -1250.50,
    interestRate: 22.99,
    minPayment: 35.00,
    dueDate: '2025-07-15',
    status: 'active'
  },
  {
    id: 'loan1',
    name: 'Student Loan',
    type: 'student loan',
    last4: '9012',
    balance: -18500.00,
    interestRate: 5.25,
    minPayment: 225.00,
    dueDate: '2025-07-01',
    status: 'active'
  },
  {
    id: 'cc2',
    name: 'Chase Freedom',
    type: 'credit card',
    last4: '3456',
    balance: -456.78,
    interestRate: 18.99,
    minPayment: 25.00,
    dueDate: '2025-07-10',
    status: 'active'
  }
];

// Mock AI advice based on debt situation
const getAIAdvice = (debtAccounts = []) => {
  if (!debtAccounts || debtAccounts.length === 0) {
    return {
      title: 'No Debt Detected',
      message: 'Great job! You currently have no debt. Keep up the good financial habits!',
      tip: 'Consider building an emergency fund if you haven\'t already.'
    };
  }

  try {
    const totalDebt = debtAccounts.reduce((sum, acc) => sum + Math.abs(acc.balance || 0), 0);
    const hasHighInterest = debtAccounts.some(acc => (acc.interestRate || 0) > 15);
    
    if (hasHighInterest) {
      return {
        title: 'High-Interest Debt Detected',
        message: `You have $${totalDebt.toLocaleString()} in debt, including some high-interest balances.`,
        tip: 'Focus on paying off high-interest debts first to save on interest payments.'
      };
    }

    return {
      title: 'Debt Management',
      message: `You have $${totalDebt.toLocaleString()} in debt.`,
      tip: 'Consider making more than the minimum payment to reduce your debt faster.'
    };
  } catch (error) {
    console.error('Error generating AI advice:', error);
    return {
      title: 'Debt Overview',
      message: 'We encountered an issue analyzing your debt. Please try again later.',
      tip: 'Check your internet connection and refresh the page if the problem persists.'
    };
  }
};

const DebtManagement = () => {
  const { user } = useUser();
  const [debtAccounts, setDebtAccounts] = useState([]);
  const [totalDebt, setTotalDebt] = useState(0);
  const [monthlyPayment, setMonthlyPayment] = useState(0);
  const [highestInterestRate, setHighestInterestRate] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [aiAdvice, setAiAdvice] = useState(null);
  const [userQuestion, setUserQuestion] = useState('');
  const [aiResponse, setAiResponse] = useState('');
  const [isScanning, setIsScanning] = useState(false);
  const [scanResults, setScanResults] = useState(null);

  // Load debt data on component mount
  useEffect(() => {
    let isMounted = true;
    
    const loadDebtData = async () => {
      try {
        // In a real app, this would be an API call
        await new Promise(resolve => setTimeout(resolve, 800));
        
        if (!isMounted) return;
        
        // Ensure mockDebtAccounts is an array before proceeding
        if (!Array.isArray(mockDebtAccounts)) {
          console.error('Invalid debt accounts data:', mockDebtAccounts);
          throw new Error('Failed to load debt data');
        }
        
        // Calculate summary metrics with null checks
        const total = mockDebtAccounts.reduce((sum, acc) => sum + Math.abs(acc?.balance || 0), 0);
        const monthly = mockDebtAccounts.reduce((sum, acc) => sum + (acc?.minPayment || 0), 0);
        const rates = mockDebtAccounts.map(acc => acc?.interestRate || 0).filter(rate => !isNaN(rate));
        const highestRate = rates.length > 0 ? Math.max(...rates) : 0;
        
        setDebtAccounts([...mockDebtAccounts]);
        setTotalDebt(total);
        setMonthlyPayment(monthly);
        setHighestInterestRate(highestRate);
        setAiAdvice(getAIAdvice(mockDebtAccounts));
      } catch (error) {
        console.error('Error loading debt data:', error);
        // Set default values in case of error
        setDebtAccounts([]);
        setTotalDebt(0);
        setMonthlyPayment(0);
        setHighestInterestRate(0);
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    loadDebtData();
    
    // Cleanup function to prevent state updates on unmounted component
    return () => {
      isMounted = false;
    };
  }, []);

  const handleQuestionSubmit = (e) => {
    e.preventDefault();
    if (!userQuestion.trim()) return;
    
    // Simulate AI response
    setAiResponse('Analyzing your question...');
    
    setTimeout(() => {
      setAiResponse(`Thank you for your question about "${userQuestion}". Based on your current debt situation, I recommend focusing on paying off high-interest debt first and considering debt consolidation if you have multiple balances.`);
      setUserQuestion('');
    }, 1500);
  };

  const handleScanAccounts = () => {
    setIsScanning(true);
    setScanResults(null);
    
    // Simulate account scanning
    setTimeout(() => {
      const potentialIssues = [];
      
      // Check for high credit card utilization
      const highUtilization = debtAccounts.filter(
        acc => acc.type === 'credit card' && (Math.abs(acc.balance) > 1000)
      );
      
      if (highUtilization.length > 0) {
        potentialIssues.push({
          type: 'high_utilization',
          message: `High balance on ${highUtilization.length} credit card${highUtilization.length > 1 ? 's' : ''}`,
          accounts: highUtilization
        });
      }
      
      // Check for high interest rates
      const highInterest = debtAccounts.filter(acc => acc.interestRate > 15);
      if (highInterest.length > 0) {
        potentialIssues.push({
          type: 'high_interest',
          message: `High interest rate${highInterest.length > 1 ? 's' : ''} detected`,
          accounts: highInterest
        });
      }
      
      setScanResults({
        timestamp: new Date().toISOString(),
        issues: potentialIssues.length > 0 ? potentialIssues : null,
        advice: potentialIssues.length > 0 
          ? 'Consider these strategies to improve your debt situation:'
          : 'No significant issues found. Your debt appears to be well-managed.'
      });
      
      setIsScanning(false);
    }, 2000);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading your debt information...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Debt Management</h1>
          <p className="mt-1 text-gray-500">Track and manage your debts in one place</p>
        </div>
        <button 
          className="mt-4 md:mt-0 px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 transition-colors"
          onClick={handleScanAccounts}
          disabled={isScanning || debtAccounts.length === 0}
        >
          {isScanning ? 'Scanning...' : 'Scan for Issues'}
        </button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <div className="bg-white p-6 rounded-xl shadow-sm border-l-4 border-indigo-500">
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-indigo-100 text-indigo-600 mr-4">
              <CurrencyDollarIcon className="h-6 w-6" />
            </div>
            <div>
              <p className="text-sm font-medium text-gray-500">Total Debt</p>
              <p className="text-2xl font-semibold text-gray-900">
                {formatCurrency(totalDebt)}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-xl shadow-sm border-l-4 border-blue-500">
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-blue-100 text-blue-600 mr-4">
              <ClockIcon className="h-6 w-6" />
            </div>
            <div>
              <p className="text-sm font-medium text-gray-500">Monthly Payment</p>
              <p className="text-2xl font-semibold text-gray-900">
                {formatCurrency(monthlyPayment)}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-xl shadow-sm border-l-4 border-red-500">
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-red-100 text-red-600 mr-4">
              <ArrowUpIcon className="h-6 w-6" />
            </div>
            <div>
              <p className="text-sm font-medium text-gray-500">Highest Interest Rate</p>
              <p className="text-2xl font-semibold text-gray-900">
                {highestInterestRate > 0 ? `${highestInterestRate}%` : 'N/A'}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-xl shadow-sm border-l-4 border-green-500">
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-green-100 text-green-600 mr-4">
              <CreditCardIcon className="h-6 w-6" />
            </div>
            <div>
              <p className="text-sm font-medium text-gray-500">Debt Accounts</p>
              <p className="text-2xl font-semibold text-gray-900">
                {debtAccounts.length}
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Debt Accounts Table */}
        <div className="lg:col-span-2">
          <div className="bg-white rounded-xl shadow-sm p-6">
            <h2 className="text-xl font-semibold mb-4 text-gray-800">Your Debts</h2>
            
            {debtAccounts.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Account</th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Balance</th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">APR</th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Min. Payment</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {debtAccounts.map((account) => (
                      <tr key={account.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="flex-shrink-0 h-10 w-10 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-600">
                              {account.type === 'credit card' ? (
                                <CreditCardIcon className="h-5 w-5" />
                              ) : (
                                <ArrowUpIcon className="h-5 w-5" />
                              )}
                            </div>
                            <div className="ml-4">
                              <div className="text-sm font-medium text-gray-900">{account.name}</div>
                              <div className="text-sm text-gray-500">•••• {account.last4}</div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium text-red-600">
                          {formatCurrency(Math.abs(account.balance))}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm text-gray-500">
                          {account.interestRate ? `${account.interestRate}%` : 'N/A'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm text-gray-500">
                          {account.minPayment ? formatCurrency(account.minPayment) : 'N/A'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-center py-8">
                <p className="text-gray-500">No debt accounts found. You're doing great!</p>
              </div>
            )}
          </div>
        </div>

        {/* AI Assistant Panel */}
        <div className="space-y-6">
          {/* AI Advice Card */}
          <div className="bg-white rounded-xl shadow-sm p-6">
            <div className="flex items-center mb-4">
              <div className="p-2 rounded-full bg-indigo-100 text-indigo-600 mr-3">
                <LightBulbIcon className="h-5 w-5" />
              </div>
              <h2 className="text-xl font-semibold text-gray-800">AI Debt Advice</h2>
            </div>
            
            {aiAdvice && (
              <div>
                <h3 className="font-medium text-gray-900 mb-2">{aiAdvice.title}</h3>
                <p className="text-gray-600 text-sm mb-3">{aiAdvice.message}</p>
                <div className="p-3 bg-indigo-50 rounded-md border-l-4 border-indigo-400">
                  <p className="text-indigo-700 text-sm">💡 {aiAdvice.tip}</p>
                </div>
              </div>
            )}

            {/* Scan Results */}
            {scanResults && (
              <div className="mt-6 pt-6 border-t border-gray-200">
                <h3 className="font-medium text-gray-900 mb-3">Scan Results</h3>
                
                {scanResults.issues ? (
                  <div>
                    <p className="text-sm text-gray-600 mb-3">{scanResults.advice}</p>
                    <ul className="space-y-3">
                      {scanResults.issues.map((issue, idx) => (
                        <li key={idx} className="p-3 bg-red-50 rounded-md border-l-4 border-red-400">
                          <p className="font-medium text-red-700">{issue.message}</p>
                          <ul className="mt-1 text-sm text-red-600">
                            {issue.accounts.map((acc, i) => (
                              <li key={i} className="flex justify-between">
                                <span>{acc.name} (••••{acc.last4})</span>
                                {issue.type === 'high_interest' && (
                                  <span>{acc.interestRate}% APR</span>
                                )}
                                {issue.type === 'high_utilization' && (
                                  <span>{formatCurrency(Math.abs(acc.balance))}</span>
                                )}
                              </li>
                            ))}
                          </ul>
                        </li>
                      ))}
                    </ul>
                  </div>
                ) : (
                  <div className="p-3 bg-green-50 rounded-md border-l-4 border-green-400">
                    <p className="text-green-700">✓ {scanResults.advice}</p>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Ask AI Section */}
          <div className="bg-white rounded-xl shadow-sm p-6">
            <h2 className="text-xl font-semibold mb-4 text-gray-800">Ask the AI Assistant</h2>
            <form onSubmit={handleQuestionSubmit} className="space-y-3">
              <div>
                <label htmlFor="question" className="block text-sm font-medium text-gray-700 mb-1">
                  Ask a question about your debt
                </label>
                <textarea
                  id="question"
                  rows="3"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 text-sm"
                  placeholder="How can I pay off my credit card debt faster?"
                  value={userQuestion}
                  onChange={(e) => setUserQuestion(e.target.value)}
                ></textarea>
              </div>
              <button
                type="submit"
                className="w-full bg-indigo-600 text-white px-4 py-2 rounded-md hover:bg-indigo-700 font-medium text-sm"
                disabled={!userQuestion.trim()}
              >
                Get AI Advice
              </button>
            </form>
            
            {aiResponse && (
              <div className="mt-4 p-3 bg-indigo-50 rounded-md border-l-4 border-indigo-400">
                <p className="text-indigo-700 text-sm">{aiResponse}</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DebtManagement;
